import { useEffect, useState } from "react";
import { Megaphone, Pause, Play, Square, ChevronRight, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUS_LABEL = {
  scheduled: "Pianificata", running: "In corso", paused: "In pausa",
  completed: "Completata", stopped: "Interrotta",
};
const STATUS_COLOR = {
  scheduled: "bg-blue-100 text-blue-700", running: "bg-brand-soft text-brand-dark",
  paused: "bg-amber-100 text-amber-700", completed: "bg-gray-100 text-gray-600",
  stopped: "bg-destructive/10 text-destructive",
};
const ITEM_COLOR = {
  pending: "text-subtle", sent: "text-brand-dark", skipped: "text-amber-600", failed: "text-destructive",
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/campaigns").then((r) => setCampaigns(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const openDetail = (cid) => {
    setOpen(cid);
    api.get(`/campaigns/${cid}`).then((r) => setDetail(r.data));
  };
  const action = async (cid, act) => {
    try { await api.post(`/campaigns/${cid}/${act}`); toast.success("Aggiornato"); load(); if (open === cid) openDetail(cid); }
    catch (e) { toast.error("Errore"); }
  };
  const toggleSkipPregen = async (c) => {
    const enabled = !c.skip_pregenerated;
    try {
      const { data } = await api.post(`/campaigns/${c.id}/skip-pregenerated`, { enabled });
      toast.success(enabled ? `Esclusione attiva${data.skipped ? ` · ${data.skipped} saltate ora` : ""}` : "Esclusione disattivata");
      load(); if (open === c.id) openDetail(c.id);
    } catch (e) { toast.error("Errore"); }
  };

  return (
    <div className="max-w-[1100px] flex flex-col gap-6" data-testid="campaigns-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-brand-dark" strokeWidth={1.5} /> Campagne email
          </h1>
          <p className="text-xs text-subtle mt-0.5">Le campagne si creano selezionando opportunità nella vista Elenco → "Crea campagna".</p>
        </div>
        <button data-testid="refresh-campaigns" onClick={load} className="flex items-center gap-1.5 text-sm text-subtle hover:text-ink transition-colors">
          <RefreshCw className="w-4 h-4" strokeWidth={1.5} /> Aggiorna
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-subtle">Caricamento…</p>
      ) : campaigns.length === 0 ? (
        <div className="bg-white border border-line rounded-[8px] p-8 text-center text-sm text-subtle">Nessuna campagna. Selezionane le opportunità dalla vista Elenco.</div>
      ) : (
        <div className="flex flex-col gap-2">
          {campaigns.map((c) => {
            const st = c.stats || {};
            return (
              <div key={c.id} data-testid={`campaign-${c.id}`} className="bg-white border border-line rounded-[8px] overflow-hidden">
                <div className="flex items-center justify-between gap-3 p-4">
                  <button onClick={() => (open === c.id ? setOpen(null) : openDetail(c.id))} className="flex items-center gap-3 min-w-0 text-left">
                    <ChevronRight className={`w-4 h-4 text-faint transition-transform ${open === c.id ? "rotate-90" : ""}`} strokeWidth={1.5} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-ink truncate">{c.name}</p>
                      <p className="text-xs text-subtle">Inizio: {fmt(c.start_at)} · {c.created_by}</p>
                    </div>
                  </button>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-subtle">Tot <b className="text-ink">{st.total || 0}</b></span>
                      <span className="text-brand-dark">Inviate <b>{st.sent || 0}</b></span>
                      <span className="text-subtle">Coda <b>{st.pending || 0}</b></span>
                      <span className="text-amber-600">Saltate <b>{st.skipped || 0}</b></span>
                      <span className="text-destructive">Errori <b>{st.failed || 0}</b></span>
                    </div>
                    <span className={`text-[11px] font-medium rounded-full px-2.5 py-1 ${STATUS_COLOR[c.status]}`}>{STATUS_LABEL[c.status]}</span>
                    <label data-testid={`skip-pregen-${c.id}`} title="Non inviare alle opportunità con email già generata manualmente" onClick={(e) => e.stopPropagation()} className="flex items-center gap-1.5 text-[11px] text-subtle cursor-pointer select-none">
                      <input type="checkbox" checked={!!c.skip_pregenerated} onChange={() => toggleSkipPregen(c)} className="accent-[#00E5A0] cursor-pointer" />
                      Escludi già generate
                    </label>
                    <div className="flex items-center gap-1">
                      {(c.status === "running" || c.status === "scheduled") && (
                        <button data-testid={`pause-${c.id}`} title="Pausa" onClick={() => action(c.id, "pause")} className="p-1.5 hover:bg-background rounded-[6px] text-subtle"><Pause className="w-4 h-4" strokeWidth={1.5} /></button>
                      )}
                      {c.status === "paused" && (
                        <button data-testid={`resume-${c.id}`} title="Riprendi" onClick={() => action(c.id, "resume")} className="p-1.5 hover:bg-background rounded-[6px] text-brand-dark"><Play className="w-4 h-4" strokeWidth={1.5} /></button>
                      )}
                      {c.status !== "completed" && c.status !== "stopped" && (
                        <button data-testid={`stop-${c.id}`} title="Interrompi" onClick={() => action(c.id, "stop")} className="p-1.5 hover:bg-background rounded-[6px] text-destructive"><Square className="w-4 h-4" strokeWidth={1.5} /></button>
                      )}
                    </div>
                  </div>
                </div>
                {open === c.id && detail && detail.campaign.id === c.id && (
                  <div className="border-t border-line px-4 py-3 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-subtle border-b border-line">
                          <th className="py-1.5 font-semibold">Azienda</th>
                          <th className="py-1.5 font-semibold">Mittente</th>
                          <th className="py-1.5 font-semibold">Destinatario</th>
                          <th className="py-1.5 font-semibold">Stato</th>
                          <th className="py-1.5 font-semibold">Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.items.map((it) => (
                          <tr key={it.id} className="border-b border-line last:border-0">
                            <td className="py-1.5 text-ink">{it.company_name || "—"}</td>
                            <td className="py-1.5 text-subtle">{it.from_mailbox_email || it.from_user_name || "—"}</td>
                            <td className="py-1.5 text-subtle">{it.to_email || "—"}</td>
                            <td className={`py-1.5 font-medium ${ITEM_COLOR[it.status]}`}>{it.status}</td>
                            <td className="py-1.5 text-faint">{it.error || (it.sent_at ? fmt(it.sent_at) : "")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
